import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, Shield, Globe, Server, Lock, Clock, Users,
  AlertTriangle, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

const AVVPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Content */}
      <main className="container mx-auto max-w-4xl px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Shield className="h-4 w-4" />
            DSGVO-konform
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">
            Vertrag zur Auftragsverarbeitung (AVV)
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            gemäß Art. 28 DSGVO für Managed Nextcloud Hosting
          </p>
        </div>

        {/* CTA for signing */}
        <Card className="border-primary/50 bg-primary/5 mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1">
                <h3 className="font-semibold mb-1">AVV digital unterzeichnen</h3>
                <p className="text-sm text-muted-foreground">
                  Als Kunde kannst du den AVV rechtssicher im Kundenportal unterzeichnen. 
                  Ihr personalisiertes PDF wird automatisch erstellt.
                </p>
              </div>
              <Button asChild>
                <Link to="/login">
                  <LogIn className="mr-2 h-4 w-4" />
                  Zum Kundenportal
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Parties */}
          <Card>
            <CardHeader>
              <CardTitle>Vertragsparteien</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Auftragnehmer (Provider)</p>
                  <p className="font-semibold">Host-On Service Provider GmbH</p>
                  <p className="text-sm text-muted-foreground">Hanauer Landstraße 204, 60314 Frankfurt am Main</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Auftraggeber (Kunde)</p>
                  <p className="font-semibold">Ihr Unternehmen / deine Person</p>
                  <p className="text-sm text-muted-foreground">gemäß Kundenkonto</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 1 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                1. Gegenstand und Dauer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                Der Auftragnehmer erbringt für den Auftraggeber Dienstleistungen im Bereich 
                <strong> Managed Nextcloud Hosting</strong> auf Basis des Hauptvertrages (AGB/Leistungsbeschreibung).
              </p>
              <p>
                Im Rahmen der Leistungserbringung hat der Auftragnehmer Zugriff auf personenbezogene Daten, 
                die der Auftraggeber in der Cloud speichert, oder kann diesen Zugriff zu Wartungszwecken nicht ausschließen.
              </p>
              <p>
                Die Laufzeit dieses Vertrages richtet sich nach der Laufzeit des Hauptvertrages. 
                Kündigt der Auftraggeber den Hosting-Vertrag, endet automatisch auch dieser AVV.
              </p>
            </CardContent>
          </Card>

          {/* Section 2 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                2. Art und Zweck der Verarbeitung
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              <p>
                Die Verarbeitung umfasst das Speichern, Auslesen, Ändern, Übermitteln (Backup), Sperren und Löschen 
                von Daten auf der Infrastruktur des Auftragnehmers. Zweck ist die Bereitstellung, Wartung und Sicherung 
                der Nextcloud-Instanz gemäß den vertraglichen Vereinbarungen.
              </p>
            </CardContent>
          </Card>

          {/* Section 3 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                3. Art der Daten und Kreis der Betroffenen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="font-semibold mb-2">3.1 Art der Daten:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Bestandsdaten (Namen, Adressen)</li>
                  <li>Kontaktdaten (E-Mail, Telefonnummern, Adressbücher)</li>
                  <li>Inhaltsdaten (Dateien, Dokumente, Bilder, Videos, Chat-Protokolle)</li>
                  <li>Termindaten (Kalender)</li>
                  <li>Verbindungsdaten (IP-Adressen, Logfiles)</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">3.2 Kreis der Betroffenen:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Kunden und Interessenten des Auftraggebers</li>
                  <li>Mitarbeiter des Auftraggebers</li>
                  <li>Externe Partner des Auftraggebers</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Section 4-5 Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                4-5. Weisungsbefugnis & Pflichten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                Der Auftraggeber ist für die Beurteilung der Zulässigkeit der Verarbeitung gemäß Art. 6 DSGVO 
                allein verantwortlich ("Verantwortlicher").
              </p>
              <p>
                Der Auftragnehmer verpflichtet sich:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>Die Verarbeitung nur in EU/EWR durchzuführen</li>
                <li>Technische und organisatorische Maßnahmen (TOMs) zu gewährleisten</li>
                <li>Nur auf Vertraulichkeit verpflichtetes Personal einzusetzen</li>
                <li>Den Auftraggeber bei Betroffenenrechten zu unterstützen</li>
              </ul>
            </CardContent>
          </Card>

          {/* TOMs Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Anlage 1: Technische und Organisatorische Maßnahmen (TOMs)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Server className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Zutrittskontrolle</p>
                    <p className="text-xs text-muted-foreground">
                      Digital Realty FRA7, ISO 27001, 24/7 Security
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Lock className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Zugangskontrolle</p>
                    <p className="text-xs text-muted-foreground">
                      SSH-Keys, Fail2Ban, VPN-only Admin
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Shield className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Verschlüsselung</p>
                    <p className="text-xs text-muted-foreground">
                      SSL/TLS, Perfect Forward Secrecy
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Clock className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Verfügbarkeit</p>
                    <p className="text-xs text-muted-foreground">
                      Tägliche Backups, 14 Tage Vorhaltezeit
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unterauftragsverarbeiter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Anlage 2: Unterauftragsverarbeiter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-semibold">Firma</th>
                      <th className="text-left py-2 px-3 font-semibold">Sitz</th>
                      <th className="text-left py-2 px-3 font-semibold">Leistung</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 px-3">Digital Realty Germany GmbH</td>
                      <td className="py-2 px-3">Deutschland</td>
                      <td className="py-2 px-3">Rechenzentrumsinfrastruktur (Colocation)</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3">InterNetX GmbH</td>
                      <td className="py-2 px-3">Deutschland</td>
                      <td className="py-2 px-3">Domain-Registrierung</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-4 italic">
                Hinweis: Zahlungsdienstleister (PayPal, Stripe) sind eigenständige Verantwortliche 
                und keine Unterauftragsverarbeiter im Sinne dieses AVV.
              </p>
            </CardContent>
          </Card>

          {/* Related Links */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <p className="font-semibold mb-4">Weitere rechtliche Dokumente:</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/agb">
                    <FileText className="mr-2 h-4 w-4" />
                    AGB
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/sla">
                    <Shield className="mr-2 h-4 w-4" />
                    SLA
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/datenschutz">
                    <Lock className="mr-2 h-4 w-4" />
                    Datenschutz
                  </Link>
                </Button>
                <Button variant="outline" asChild className="justify-start">
                  <Link to="/impressum">
                    <FileText className="mr-2 h-4 w-4" />
                    Impressum
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center pt-8">
            <p className="text-muted-foreground mb-4">
              Bereit für DSGVO-konformes Cloud-Hosting?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/checkout">
                  Jetzt Wolke erstellen
                </Link>
              </Button>
              <Button variant="outline" asChild size="lg">
                <Link to="/login">
                  <LogIn className="mr-2 h-4 w-4" />
                  AVV im Portal unterzeichnen
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
};

export default AVVPage;
